import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import type { ApprovalFormValues } from '@/components/Approval/ApprovalCreate/types';
import useRelatedApprovalMutations from '@/hooks/approval/useRelatedApprovalMutations';
import type { AddRelationDocument } from '@/types/approval';

/**
 * 품의 저장 성공 후 호출 — 폼 state(relatedApprovals/deletedRelatedDocIds)를 백엔드에 동기화.
 * 레거시와 동일하게 매번 selected 전체를 전송, 백엔드가 idempotent 처리한다고 가정.
 * 성공 시 baseline(initialRelatedDocIds/deletedRelatedDocIds)을 서버 반영 상태로 커밋 —
 * 세션 중 저장으로 서버에 생긴 항목도 이후 삭제가 추적되고, 처리된 삭제가 재전송되지 않는다.
 */
const useApprovalRelationSync = () => {
  const { getValues, setValue } = useFormContext<ApprovalFormValues>();
  const { addRelations, deleteRelations } = useRelatedApprovalMutations();

  return useCallback(
    async (savedDocId: string) => {
      const { relatedApprovals, deletedRelatedDocIds } = getValues();

      if (deletedRelatedDocIds.length > 0) {
        await deleteRelations(savedDocId, deletedRelatedDocIds);
      }

      const documents: AddRelationDocument[] = [
        ...relatedApprovals.parents.map((it) => ({
          id: it.docId,
          relationType: 'parents' as const,
        })),
        ...relatedApprovals.children.map((it) => ({
          id: it.docId,
          relationType: 'children' as const,
        })),
        ...relatedApprovals.related.map((it) => ({
          id: it.docId,
          relationType: 'related' as const,
        })),
      ];

      if (documents.length > 0) {
        await addRelations(savedDocId, documents);
      }

      // initial=전송분 전체(서버 진실) — 현재 선택과 교차해 좁히면 재추가→재삭제 시 추적이 다시 끊긴다.
      const sentIds = documents.map((it) => it.id);
      setValue('initialRelatedDocIds', sentIds, { shouldDirty: false });
      // 요청 중 조작 흡수: 처리된 삭제(전송 시점 캡처분)만 차감하고,
      // 전송됐지만 커밋 시점 선택에서 빠진 항목은 서버에 남아 있으므로 삭제 대상으로 편입.
      const current = getValues('relatedApprovals');
      const currentIds = new Set(
        [...current.parents, ...current.children, ...current.related].map(
          (it) => it.docId,
        ),
      );
      setValue(
        'deletedRelatedDocIds',
        [
          ...new Set([
            ...getValues('deletedRelatedDocIds').filter(
              (id) => !deletedRelatedDocIds.includes(id),
            ),
            ...sentIds.filter((id) => !currentIds.has(id)),
          ]),
        ],
        { shouldDirty: false },
      );
    },
    [getValues, setValue, addRelations, deleteRelations],
  );
};

export default useApprovalRelationSync;
